// Same-origin build downloads. Each platform URL is served by a Pages Function
// that streams the build out of R2 with an attachment disposition, so every
// "Download" affordance on the site downloads the file in place, with no hop to a
// GitHub release page.
export const DOWNLOAD_URL = '/download';
// What /download saves as: the per-user Windows installer. The desktop
// manifest's flat bundle_name names the legacy portable 7z instead (old clients
// update from /download/portable), so the site never reads the name from there.
export const WINDOWS_INSTALLER_NAME = 'StreamNook-Setup.exe';
export const MAC_DOWNLOAD_URL = '/download/mac';
export const LINUX_DOWNLOAD_URL = '/download/linux';
export const ANDROID_DOWNLOAD_URL = '/download/android';

// The iPhone beta's TestFlight public link (iPhone only).
export const TESTFLIGHT_URL = 'https://testflight.apple.com/join/QjXGJZs9';
// Apple's TestFlight app on the App Store, which the invite needs installed.
export const TESTFLIGHT_APP_URL = 'https://apps.apple.com/app/testflight/id899247664';

// The add-repo page for StreamNook's own F-Droid repository. It is a Pages
// Function, not an SPA route, so link to it with a plain anchor (a full load).
export const FDROID_URL = '/fdroid';

// Version metadata per platform. Both are thin reads of a manifest object that
// sits in R2 next to the build it describes. The macOS build has no manifest of
// its own: it rides in the desktop manifest under `platforms`, because the
// desktop client's updater reads one manifest for every desktop OS.
export const UPDATE_MANIFEST_URL = '/api/v1/update';
export const ANDROID_MANIFEST_URL = '/api/v1/update-android';

// "View source / all releases" links still point at GitHub on purpose; that's
// a browse action, not the download. Kept here so the two intents are explicit.
export const RELEASES_URL = 'https://github.com/StreamNook/StreamNook/releases/latest';

// Linux releases are paused: a release ships a Linux build only while the repo
// variable LINUX_RELEASE is "true", and without one the desktop manifest has no
// linux-x86_64 entry. The last AppImage (8.7.1) stays at /download/linux, so the
// Linux card offers it under this version. Update both when Linux resumes.
export const LINUX_LAST_VERSION = '8.7.1';
export const LINUX_LAST_RELEASE_URL = 'https://github.com/StreamNook/StreamNook/releases/tag/v8.7.1';

// The Android build ships outside any app store, so new builds are announced in
// the Discord rather than through a store listing.
export const DISCORD_URL = 'https://discord.gg/2xvuF9TES7';

/** The build a visitor can install on the device they are browsing from. */
export type VisitorPlatform = 'windows' | 'mac' | 'linux' | 'android' | 'iphone';

/**
 * Best guess at the visitor's platform, so the page can lead with the build
 * they can actually install. Chrome on Android keeps "Android" in the UA
 * string even under the reduced-UA rollout; Safari and Chrome on a Mac keep
 * "Macintosh". The check is a presentation hint only, and every platform stays
 * one click away whichever way it resolves.
 *
 * ORDER MATTERS between the iPhone and Mac checks. Every iPhone browser's UA
 * ends in `like Mac OS X` (`iPhone; CPU iPhone OS 26_0 like Mac OS X`), so the
 * Mac test placed first would offer every iPhone the Mac build. Only "iPhone"
 * or "iPod" in the UA means the iPhone build. An iPad is never one: iPadOS
 * Safari sends the desktop-class "Macintosh" UA (told apart from a real Mac
 * only by its touch points) and other iPad browsers say "iPad", so both fall
 * through to the Mac button as before, which is harmless because there is no
 * iPad build to offer instead.
 *
 * ORDER MATTERS between the Android and Linux checks too. Android's UA contains
 * "Linux" as well as "Android" (it is `Linux; Android 14; ...`), so a bare
 * /linux/ test placed first would hand every Android phone the AppImage. The
 * Android branch therefore stays above it, and the Linux branch additionally
 * excludes Android rather than relying on order alone, because a reader who
 * reorders these lines should not be able to reintroduce that silently.
 */
export function detectVisitorPlatform(): VisitorPlatform {
  if (typeof navigator === 'undefined') return 'windows';
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return 'android';
  if (/iPhone|iPod/.test(ua)) return 'iphone';
  if (/Macintosh|Mac OS X/.test(ua)) return 'mac';
  if (/linux|x11/i.test(ua) && !/android/i.test(ua)) return 'linux';
  return 'windows';
}
